import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Subject, combineLatest } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  AdminLeadApiService,
  AdminLeadListQuery,
  AdminLeadResponse,
  formatLeadPhone,
  StaffLeadSection,
} from '@nebalkon-workspace/lead-api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  NotButton,
  NotInput,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import { bindStaffPageTitleCount } from '../admin/staff-page-title-count.util';
import {
  formatStaffLeadShortId,
  isStaffLeadSection,
  STAFF_LEAD_SECTION_NAV,
  STAFF_LEAD_STATUS_FILTERS,
  staffLeadKindLabelKey,
  staffLeadStatusLabelKey,
  StaffLeadStatusFilter,
} from './staff-lead-section.util';
import { StaffLeadsStatsService } from './staff-leads-stats.service';

@Component({
  selector: 'lib-staff-leads-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    FormsModule,
    NotButton,
    NotInput,
    DatePipe,
    RouterLink,
  ],
  templateUrl: './staff-leads-list.component.html',
  styleUrl: './staff-leads-list.component.scss',
})
export class StaffLeadsListComponent implements OnInit {
  readonly embeddedInCabinetPage = input(false);

  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #api = inject(AdminLeadApiService);
  readonly #statsService = inject(StaffLeadsStatsService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly sections = STAFF_LEAD_SECTION_NAV;
  protected readonly statusFilters = STAFF_LEAD_STATUS_FILTERS;

  protected readonly section = signal<StaffLeadSection>(
    STAFF_LEAD_SECTION_NAV[0].id,
  );
  protected readonly statusFilter = signal<StaffLeadStatusFilter>('all');
  protected readonly searchQuery = signal('');
  private readonly searchSubject = new Subject<string>();

  protected readonly hasFiltersActive = computed(
    () =>
      this.statusFilter() !== 'all' || this.searchQuery().trim() !== '',
  );

  protected readonly items = signal<AdminLeadResponse[]>([]);
  protected readonly total = signal(0);
  protected readonly loading = signal(true);
  protected readonly loadingMore = signal(false);
  protected readonly accessDenied = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly hasMore = signal(false);
  protected readonly nextCursor = signal<string | null>(null);

  protected readonly displayCount = computed(() =>
    this.loading() ? null : this.total(),
  );

  constructor() {
    bindStaffPageTitleCount(this.embeddedInCabinetPage, this.displayCount);
  }

  public ngOnInit(): void {
    this.#statsService.ensureLoaded();

    combineLatest([this.#route.paramMap, this.#route.queryParamMap])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(([params, qm]) => {
        const sectionParam = params.get('section');
        if (!isStaffLeadSection(sectionParam)) {
          void this.#router.navigate([this.#cabinetPaths.staff, 'leads']);
          return;
        }

        this.section.set(sectionParam);
        this.#applyStatusFromQuery(qm.get('status'));
        this.#loadFirstPage();
      });

    this.searchSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#loadFirstPage());
  }

  protected sectionLink(section: StaffLeadSection): string[] {
    return [this.#cabinetPaths.staff, 'leads', section];
  }

  protected setStatusFilter(id: StaffLeadStatusFilter): void {
    if (this.statusFilter() === id) {
      return;
    }

    void this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { status: id === 'all' ? null : id },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.searchSubject.next(value);
  }

  protected onOpen(id: string): void {
    void this.#router.navigate([
      this.#cabinetPaths.staff,
      'leads',
      this.section(),
      id,
    ]);
  }

  protected loadMore(): void {
    const cursor = this.nextCursor();
    if (!cursor || this.loadingMore()) {
      return;
    }

    this.loadingMore.set(true);
    this.#api.listLeads(this.#buildQuery(cursor)).subscribe({
      next: (res) => {
        this.items.update((prev) => [...prev, ...res.data]);
        this.nextCursor.set(res.next_cursor ?? null);
        this.hasMore.set(Boolean(res.next_cursor));
        this.loadingMore.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.loadingMore.set(false);
        this.#toastError(err);
      },
    });
  }

  protected cardPrimaryLine(row: AdminLeadResponse): string {
    return row.title?.trim() || row.guest_name?.trim() || formatLeadPhone(row.phone);
  }

  protected cardContactLine(row: AdminLeadResponse): string | null {
    const name = row.guest_name?.trim();
    const phone = formatLeadPhone(row.phone);
    if (name && phone) {
      return `${name} · ${phone}`;
    }
    return name || phone || null;
  }

  protected readonly formatStaffLeadShortId = formatStaffLeadShortId;
  protected readonly kindLabelKey = staffLeadKindLabelKey;
  protected readonly statusLabelKey = staffLeadStatusLabelKey;

  #applyStatusFromQuery(value: string | null): void {
    const allowed = STAFF_LEAD_STATUS_FILTERS.map((f) => f.id);
    if (value && allowed.includes(value as StaffLeadStatusFilter)) {
      this.statusFilter.set(value as StaffLeadStatusFilter);
      return;
    }
    this.statusFilter.set('all');
  }

  #buildQuery(cursor?: string): AdminLeadListQuery {
    const query: AdminLeadListQuery = {
      section: this.section(),
      limit: 30,
    };

    const status = this.statusFilter();
    if (status !== 'all') {
      query.status = status;
    }

    const search = this.searchQuery().trim();
    if (search) {
      query.search = search;
    }

    if (cursor) {
      query.cursor = cursor;
    }

    return query;
  }

  #loadFirstPage(): void {
    this.loading.set(true);
    this.error.set(null);
    this.accessDenied.set(false);

    this.#api.listLeads(this.#buildQuery()).subscribe({
      next: (res) => {
        this.items.set(res.data);
        this.total.set(res.total);
        this.nextCursor.set(res.next_cursor ?? null);
        this.hasMore.set(Boolean(res.next_cursor));
        this.loading.set(false);
        this.#statsService.refresh();
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        if (err.status === 403) {
          this.accessDenied.set(true);
        } else {
          this.error.set(this.#translate.instant('STAFF.LEADS.STATE.LOAD_ERROR'));
        }
      },
    });
  }

  #toastError(err: HttpErrorResponse): void {
    const msg =
      err.status === 403
        ? this.#translate.instant('STAFF.LEADS.STATE.ACCESS_DENIED')
        : this.#translate.instant('STAFF.LEADS.STATE.LOAD_ERROR');
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [msg],
    });
  }
}
